import React from 'react';
import { Location } from 'common/types';
import { Box, Select, MenuItem, Checkbox, FormControlLabel, Chip } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { hashByUniqueField } from 'common/utils/arrays';
import { useTranslation } from 'services/localization';

const ALL_STORES = '__ALL_STORES__';
interface Props {
	value: string[] | undefined;
	onChange: (value: string[] | undefined) => void;
	options: Location[];
	disabled?: boolean;
	allowAllLocations?: boolean;
}
const LocationsSelect = (props: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const locationsById = hashByUniqueField(props.options, 'id');
	const value = props.value ?? [];
	const valueWithoutDeleted = value.filter((locationId) => !!locationsById[locationId]);

	const renderValue = (value: unknown) => {
		const _value = value as string[] | undefined;
		if (!_value?.length) {
			return <Chip label={t('common:shopLocation.allStores', 'All stores')} />;
		}

		return (
			<Box display="flex" flexDirection="row" flexWrap="wrap">
				{_value.map((locationId) => {
					const locationName =
						locationsById[locationId]?.name ?? t('users.deletedStore', '<deleted store>');
					if (!locationName) return null;

					return (
						<Box mr="2px" my="1px" key={locationId}>
							<Chip label={locationName} />
						</Box>
					);
				})}
			</Box>
		);
	};

	const toggleAllStores = () => {
		if (!!value?.length) {
			props.onChange(undefined);
		}
	};

	const toggleLocation = (locationId: string) => () => {
		if (value.includes(locationId)) {
			const newValue = valueWithoutDeleted.filter((l) => l !== locationId);
			props.onChange(newValue.length === 0 ? undefined : newValue);
		} else {
			props.onChange([...valueWithoutDeleted, locationId]);
		}
	};

	return (
		<Select
			fullWidth
			displayEmpty
			multiple
			variant="outlined"
			disabled={props.disabled}
			value={value}
			renderValue={renderValue}
			classes={{ disabled: classes.selectDisabled }}
			MenuProps={{
				classes: { paper: classes.menuPaper },
				anchorOrigin: {
					vertical: 'bottom',
					horizontal: 'right',
				},
				transformOrigin: {
					vertical: 'top',
					horizontal: 'right',
				},
			}}
		>
			{props.allowAllLocations && (
				<MenuItem value={ALL_STORES} onClick={toggleAllStores}>
					<FormControlLabel
						className={classes.formControl}
						control={
							<Checkbox
								disabled={value.length === 0}
								checked={value.length === 0}
								color="primary"
							/>
						}
						label={t('common:shopLocation.allStores', 'All stores')}
					/>
				</MenuItem>
			)}

			{props.options.map((location) => {
				const checked = value.includes(location.id);
				const isLastChecked = checked && value.length === 1;
				return (
					<MenuItem
						key={location.id}
						value={location.id}
						onClick={toggleLocation(location.id)}
						disabled={isLastChecked && !props.allowAllLocations}
					>
						<FormControlLabel
							className={classes.formControl}
							control={<Checkbox checked={value.includes(location.id)} color="primary" />}
							label={location.name}
						/>
					</MenuItem>
				);
			})}
		</Select>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		selectDisabled: {
			cursor: 'not-allowed !important',
		},
		menuPaper: {
			maxWidth: 536,
			width: `calc(100% - ${theme.spacing(4)})`,
		},
		formControl: {
			pointerEvents: 'none',
		},
	}),
);

export default LocationsSelect;
